import {
    type ReactElement,
    type FC
} from "react";
import type IEmbeddedMenuButton from "./type";
import type {
    EmbeddedMenuButtonRenderConstantType
} from "./type";
import {
    NCoreUIKitLocalize
} from "../../../../core/hooks";
import {
    useActiveRouteName,
    isRouteActive
} from "../../../../helpers/navigation";
import type {
    EmbeddedMenuButton as EmbeddedMenuButtonType
} from "../../type";
import {
    getMenuButtonEndIcon,
    getMenuButtonTitle,
    navigateMenuButton
} from "../../../menu/util";
import {
    getEmbeddedMenuPressableProps
} from "../../util";
import Sidebar from "../../../sidebar";

const getEmbeddedMenuBadge = (buttonItem: EmbeddedMenuButtonType): number | undefined => {
    return typeof buttonItem.badge === "number" && buttonItem.badge > 0 ? buttonItem.badge : undefined;
};

const EmbeddedMenuButton: FC<IEmbeddedMenuButton> = ({
    activeRouteName: activeRouteNameProp,
    updateMenuButtonCollapse,
    customLocalize,
    isMenuExpanded,
    closeAction,
    customTheme,
    buttonItem,
    navigation,
    depthMap,
    id
}) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const ownActiveRouteName = useActiveRouteName({
        navigation: activeRouteNameProp === undefined ? navigation : undefined
    });

    const activeRouteName = activeRouteNameProp ?? ownActiveRouteName;

    const getIsActive = (currentItem: EmbeddedMenuButtonType): boolean => {
        return currentItem.props?.isSelected ?? isRouteActive({
            item: currentItem,
            activeRouteName
        });
    };

    const getKey = (currentDepthMap: Array<number>): string => {
        return `embedded-menu-button-${id}-${currentDepthMap.join("-")}`;
    };

    const handleNavigate = (currentItem: EmbeddedMenuButtonType) => {
        navigateMenuButton({
            buttonItem: currentItem,
            closeAction,
            navigation
        });
    };

    const renderSubButton = ({
        depthMap: currentDepthMap,
        buttonItem: currentItem
    }: EmbeddedMenuButtonRenderConstantType): ReactElement | null => {
        if(currentItem.icon || currentItem.props?.icon || currentItem.subButtons?.length || currentItem.props?.subTitle || currentItem.props?.rightIcon || currentItem.isShowRedirectIcon) {
            return renderButton({
                depthMap: currentDepthMap,
                buttonItem: currentItem
            });
        }

        const badge = getEmbeddedMenuBadge(currentItem);

        return <Sidebar.MenuSubItem
            key={getKey(currentDepthMap)}
        >
            <Sidebar.MenuSubButton
                {...getEmbeddedMenuPressableProps({
                    props: currentItem.props
                })}
                customTheme={currentItem.props?.customTheme ?? customTheme}
                onPress={() => handleNavigate(currentItem)}
                isDisabled={currentItem.props?.isDisabled}
                isActive={getIsActive(currentItem)}
                style={currentItem.props?.style}
                title={getMenuButtonTitle({
                    buttonItem: currentItem,
                    localize
                })}
                size="medium"
            />
            {
                badge !== undefined && <Sidebar.MenuBadge
                    label={localize("badge-unread")}
                    count={badge}
                />
            }
        </Sidebar.MenuSubItem>;
    };

    const renderButton = ({
        depthMap: currentDepthMap,
        buttonItem: currentItem
    }: EmbeddedMenuButtonRenderConstantType): ReactElement | null => {
        const icon = currentItem.icon ?? currentItem.props?.icon;

        const isRoot = currentDepthMap.length === 1;

        if(isRoot && !isMenuExpanded && !icon) {
            return null;
        }

        const hasSubButtons = !!currentItem.subButtons && currentItem.subButtons.length > 0;

        const isCollapsible = !!currentItem.isCollapsible;

        const badge = getEmbeddedMenuBadge(currentItem);

        const handlePress = () => {
            if(isCollapsible) {
                updateMenuButtonCollapse({
                    status: !currentItem.isCollapse,
                    depthMap: currentDepthMap,
                    buttonItem: currentItem
                });

                return;
            }

            handleNavigate(currentItem);
        };

        return <Sidebar.MenuItem
            isExpanded={isCollapsible ? !!currentItem.isCollapse : true}
            onExpandedChange={(status) => updateMenuButtonCollapse({
                depthMap: currentDepthMap,
                buttonItem: currentItem,
                status
            })}
            isCollapsible={isCollapsible}
            key={getKey(currentDepthMap)}
        >
            <Sidebar.MenuButton
                {...getEmbeddedMenuPressableProps({
                    props: currentItem.props
                })}
                onPress={hasSubButtons && (isCollapsible || !isMenuExpanded) ? undefined : handlePress}
                customTheme={currentItem.props?.customTheme ?? customTheme}
                isDisabled={currentItem.props?.isDisabled}
                tooltip={hasSubButtons ? true : undefined}
                subTitle={currentItem.props?.subTitle}
                isActive={getIsActive(currentItem)}
                size={isRoot ? "large" : "medium"}
                style={currentItem.props?.style}
                endIcon={getMenuButtonEndIcon({
                    buttonItem: currentItem
                })}
                title={getMenuButtonTitle({
                    buttonItem: currentItem,
                    localize
                })}
                icon={icon}
            />
            {
                badge !== undefined && <Sidebar.MenuBadge
                    label={localize("badge-unread")}
                    count={badge}
                />
            }
            {
                hasSubButtons && <Sidebar.MenuSub>
                    {
                        currentItem.subButtons?.map((subButtonItem, subButtonIndex) => renderSubButton({
                            buttonItem: subButtonItem,
                            depthMap: [
                                ...currentDepthMap,
                                subButtonIndex
                            ]
                        }))
                    }
                </Sidebar.MenuSub>
            }
        </Sidebar.MenuItem>;
    };

    return renderButton({
        buttonItem,
        depthMap
    });
};
export default EmbeddedMenuButton;
